import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { superFetch } from '@/lib/super-admin-api';
import type { Plan } from '@/lib/super-admin-types';

interface FormState {
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  telefone: string;
  email: string;
  ownerName: string;
  planId: string;
  adminLogin: string;
  adminPassword: string;
}

const initialForm: FormState = {
  nomeFantasia: '',
  razaoSocial: '',
  cnpj: '',
  telefone: '',
  email: '',
  ownerName: '',
  planId: '',
  adminLogin: '',
  adminPassword: '',
};

export function TenantCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    superFetch('/api/super-admin/plans')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  function formatCNPJ(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.nomeFantasia || form.nomeFantasia.length < 2) {
      newErrors.nomeFantasia = 'Nome fantasia é obrigatório (mínimo 2 caracteres)';
    }
    if (!form.razaoSocial || form.razaoSocial.length < 2) {
      newErrors.razaoSocial = 'Razão social é obrigatória (mínimo 2 caracteres)';
    }

    const cnpjDigits = form.cnpj.replace(/\D/g, '');
    if (cnpjDigits.length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    if (!form.telefone) {
      newErrors.telefone = 'Telefone é obrigatório';
    }

    if (!form.planId) {
      newErrors.planId = 'Selecione um plano';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setCreating(true);
    try {
      const res = await superFetch('/api/super-admin/tenants', {
        method: 'POST',
        body: JSON.stringify({
          nomeFantasia: form.nomeFantasia,
          razaoSocial: form.razaoSocial || form.nomeFantasia,
          cnpj: form.cnpj,
          telefone: form.telefone,
          email: form.email,
          ownerName: form.ownerName || form.nomeFantasia,
          planId: form.planId,
          adminLogin: form.adminLogin || 'admin',
          adminPassword: form.adminPassword || 'admin123',
        }),
      });

      if (res.ok) {
        const tenant = await res.json();
        toast.success('Estabelecimento criado com sucesso!');
        navigate(`/super-admin/tenants/${tenant.id}`);
      } else if (res.status === 409) {
        toast.error('CNPJ já cadastrado no sistema');
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao criar estabelecimento');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setCreating(false);
    }
  }

  const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' };
  const inputErrorStyle = { ...inputStyle, border: '1px solid #ef4444' };

  function updateField(field: keyof FormState, value: string) {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* Breadcrumb + Back */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/super-admin/tenants')}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:opacity-80"
          style={{ background: '#334155', color: '#e2e8f0' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
        <div className="flex items-center gap-2 text-sm" style={{ color: '#64748b' }}>
          <span>Estabelecimentos</span>
          <span>/</span>
          <span style={{ color: '#f1f5f9' }}>Novo</span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: '#10b98120' }}>
          <Building2 className="h-5 w-5" style={{ color: '#10b981' }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>Novo Estabelecimento</h1>
          <p className="text-base mt-0.5" style={{ color: '#94a3b8' }}>Preencha os dados para criar um novo tenant</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Business Info */}
        <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#f1f5f9' }}>Dados do Estabelecimento</h3>
          <div className="space-y-4">
            <FormField label="Nome Fantasia *" error={errors.nomeFantasia}>
              <input
                type="text"
                value={form.nomeFantasia}
                onChange={(e) => updateField('nomeFantasia', e.target.value)}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={errors.nomeFantasia ? inputErrorStyle : inputStyle}
                placeholder="Ex: Mercadinho do Zé"
              />
            </FormField>

            <FormField label="Razão Social *" error={errors.razaoSocial}>
              <input
                type="text"
                value={form.razaoSocial}
                onChange={(e) => updateField('razaoSocial', e.target.value)}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={errors.razaoSocial ? inputErrorStyle : inputStyle}
                placeholder="Ex: José da Silva Comércio LTDA"
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="CNPJ *" error={errors.cnpj}>
                <input
                  type="text"
                  value={form.cnpj}
                  onChange={(e) => updateField('cnpj', formatCNPJ(e.target.value))}
                  className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                  style={errors.cnpj ? inputErrorStyle : inputStyle}
                  placeholder="00.000.000/0000-00"
                />
              </FormField>

              <FormField label="Telefone *" error={errors.telefone}>
                <input
                  type="text"
                  value={form.telefone}
                  onChange={(e) => updateField('telefone', formatPhone(e.target.value))}
                  className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                  style={errors.telefone ? inputErrorStyle : inputStyle}
                  placeholder="(00) 00000-0000"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                  style={inputStyle}
                  placeholder="contato@empresa.com"
                />
              </FormField>

              <FormField label="Responsável">
                <input
                  type="text"
                  value={form.ownerName}
                  onChange={(e) => updateField('ownerName', e.target.value)}
                  className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                  style={inputStyle}
                  placeholder="Nome do proprietário"
                />
              </FormField>
            </div>

            <FormField label="Plano *" error={errors.planId}>
              <select
                value={form.planId}
                onChange={(e) => updateField('planId', e.target.value)}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={errors.planId ? inputErrorStyle : inputStyle}
              >
                <option value="">Selecione um plano</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — R${Number(p.price).toFixed(0)}/mês
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </div>

        {/* Admin Credentials */}
        <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#f1f5f9' }}>Credenciais do Admin</h3>
          <p className="text-sm mb-4" style={{ color: '#64748b' }}>
            Usuário e senha para o primeiro acesso do estabelecimento
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Login">
              <input
                type="text"
                value={form.adminLogin}
                onChange={(e) => updateField('adminLogin', e.target.value)}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={inputStyle}
                placeholder="admin"
              />
            </FormField>

            <FormField label="Senha">
              <input
                type="text"
                value={form.adminPassword}
                onChange={(e) => updateField('adminPassword', e.target.value)}
                className="w-full rounded-lg px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
                style={inputStyle}
                placeholder="admin123"
              />
            </FormField>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/super-admin/tenants')}
            className="rounded-lg px-5 py-2.5 text-base font-medium transition-colors hover:opacity-80"
            style={{ color: '#94a3b8' }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={creating}
            className="rounded-lg px-5 py-2.5 text-base font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ background: '#10b981' }}
          >
            {creating ? 'Criando...' : 'Criar Estabelecimento'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block" style={{ color: '#94a3b8' }}>
        {label}
      </label>
      {children}
      {error && (
        <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{error}</p>
      )}
    </div>
  );
}
